/**
 * Client for POST /api/v2/posts (UnifiedPostComposer) and forum feed-media upload helpers.
 * Thread comments use club/universal APIs from `clubsApi.js`, not this module.
 * Auth: Bearer from localStorage/sessionStorage + dev x-user-id (same pattern as clubsApi).
 */

const API_BASE = process.env.REACT_APP_API_BASE || "";
const POSTS_V2 = "/api/v2/posts";

/** POST multipart; same mount as club uploads (`server.js` app.use("/api/upload", ...)). */
export const FORUM_FEED_MEDIA_UPLOAD = "/api/upload/forum/feed-media";

function getDevUserIdHeader() {
  if (process.env.NODE_ENV !== "development") return {};
  const id = localStorage.getItem("DEV_USER_ID");
  return id ? { "x-user-id": id } : {};
}

function getAuthHeader() {
  const keys = ["accessToken", "token", "jwt", "authToken"];
  let token = null;
  try {
    for (const k of keys) {
      token = localStorage.getItem(k);
      if (token) break;
    }
    if (!token) {
      for (const k of keys) {
        token = sessionStorage.getItem(k);
        if (token) break;
      }
    }
  } catch {
    /* ignore */
  }
  if (!token) return {};
  return { Authorization: `Bearer ${token}` };
}

function normalizePath(path) {
  const raw = String(path || "");
  if (/^https?:\/\//i.test(raw)) return raw;
  const p = raw.startsWith("/") ? raw : `/${raw}`;
  if (p.startsWith("/api/api/")) return p.replace(/^\/api\/api\//, "/api/");
  if (p.startsWith("/api/")) return p;
  if (p.startsWith("/arthouse/")) return `/api${p}`;
  return p;
}

function isFormDataBody(body) {
  if (body == null || typeof body !== "object") return false;
  if (typeof FormData !== "undefined" && body instanceof FormData) return true;
  return typeof body.append === "function" && typeof body.get === "function";
}

async function apiFetch(path, options = {}) {
  const normalized = normalizePath(path);
  const url = /^https?:\/\//i.test(normalized) ? normalized : `${API_BASE}${normalized}`;

  const method = (options.method || "GET").toUpperCase();
  const hasBody = options.body != null;
  const isFormData = isFormDataBody(options.body);

  const headers = {
    ...(hasBody && !isFormData ? { "Content-Type": "application/json" } : {}),
    ...(options.headers || {}),
    ...getDevUserIdHeader(),
    ...getAuthHeader(),
  };

  if (isFormData) {
    delete headers["Content-Type"];
    delete headers["content-type"];
  }

  const res = await fetch(url, {
    credentials: "include",
    ...options,
    method,
    headers,
  });

  if (res.status === 204) return null;

  const contentType = res.headers.get("content-type") || "";
  const isJson = contentType.includes("application/json");
  const data = isJson ? await res.json().catch(() => null) : await res.text();

  if (!res.ok) {
    let msg =
      typeof data === "string"
        ? data
        : data?.message || `Request failed (${res.status})`;
    if (data && typeof data === "object") {
      if (typeof data.error === "string") msg = data.error;
      else if (data.error && typeof data.error === "object" && data.error.message != null) {
        msg = String(data.error.message);
      }
    }
    const err = new Error(msg);
    err.status = res.status;
    err.data = data;
    err.code = data?.code;
    err.path = normalized;
    throw err;
  }

  return data;
}

/**
 * Canonical create: POST /api/v2/posts (forum_posts + optional forum_post_attachments).
 * @param {Record<string, unknown>} body
 * @param {AbortSignal} [signal]
 * @returns {Promise<{ post: object, attachments: object[] }>}
 */
export async function createPost(body, signal) {
  if (!body || typeof body !== "object") {
    const err = new Error("Request body is required.");
    err.status = 400;
    throw err;
  }
  return apiFetch(POSTS_V2, {
    method: "POST",
    body: JSON.stringify(body),
    signal,
  });
}

/**
 * Upload one image for a global / Community unified post (R2).
 * Same envelope as club feed-media: `{ success, attachment }` on success.
 *
 * @param {{ file: Blob, signal?: AbortSignal }} params
 */
export async function uploadForumPostFeedMedia({ file, signal } = {}) {
  if (!file) {
    const err = new Error("Missing file.");
    err.status = 400;
    throw err;
  }
  const fd = new FormData();
  fd.append("file", file);
  return apiFetch(FORUM_FEED_MEDIA_UPLOAD, {
    method: "POST",
    body: fd,
    signal,
  });
}

/**
 * Normalize forum/club feed-media JSON into one attachment object (or null).
 * Compatible with `normalizeClubFeedUploadAttachment` response shapes.
 * @param {unknown} body
 * @returns {object | null}
 */
export function normalizeFeedMediaUploadResponse(body) {
  if (!body || typeof body !== "object") return null;
  const b = body;
  const nested =
    b.attachment && typeof b.attachment === "object"
      ? b.attachment
      : b.data && typeof b.data === "object" && b.data.attachment
        ? b.data.attachment
        : null;
  if (!nested) return null;
  const url = nested.url ?? nested.image_url ?? nested.imageUrl ?? null;
  if (!url || typeof url !== "string") return null;
  return {
    kind: String(nested.kind || "image").toLowerCase() || "image",
    url: String(url).trim(),
    r2_key: nested.r2_key != null ? String(nested.r2_key).trim() : null,
    mime: nested.mime != null ? String(nested.mime).trim() : null,
    size_bytes:
      nested.size_bytes != null && Number.isFinite(Number(nested.size_bytes))
        ? Math.trunc(Number(nested.size_bytes))
        : null,
    width:
      nested.width != null && Number.isFinite(Number(nested.width)) ? Math.trunc(Number(nested.width)) : null,
    height:
      nested.height != null && Number.isFinite(Number(nested.height))
        ? Math.trunc(Number(nested.height))
        : null,
    duration_ms:
      nested.duration_ms != null && Number.isFinite(Number(nested.duration_ms))
        ? Math.trunc(Number(nested.duration_ms))
        : null,
    artwork_id:
      nested.artwork_id != null && Number.isFinite(Number(nested.artwork_id))
        ? Math.trunc(Number(nested.artwork_id))
        : null,
    sort_order:
      nested.sort_order != null && Number.isFinite(Number(nested.sort_order))
        ? Math.trunc(Number(nested.sort_order))
        : 0,
  };
}

/**
 * Strip to the payload shape expected by POST /api/v2/posts `attachments[]`.
 * @param {object | null} att from {@link normalizeFeedMediaUploadResponse}
 * @param {number} [sortOrder]
 */
export function toV2CreateAttachmentRow(att, sortOrder = 0) {
  if (!att || typeof att !== "object") return null;
  const url = String(att.url || "").trim();
  if (!url) return null;
  const row = {
    kind: "image",
    url,
    sort_order: Number.isFinite(Number(sortOrder)) ? Math.trunc(Number(sortOrder)) : 0,
  };
  if (att.r2_key != null && String(att.r2_key).trim() !== "") row.r2_key = String(att.r2_key).trim();
  if (att.mime != null && String(att.mime).trim() !== "") row.mime = String(att.mime).trim();
  if (att.size_bytes != null && Number.isFinite(Number(att.size_bytes))) row.size_bytes = Math.trunc(Number(att.size_bytes));
  if (att.width != null && Number.isFinite(Number(att.width))) row.width = Math.trunc(Number(att.width));
  if (att.height != null && Number.isFinite(Number(att.height))) row.height = Math.trunc(Number(att.height));
  if (att.duration_ms != null && Number.isFinite(Number(att.duration_ms))) {
    row.duration_ms = Math.trunc(Number(att.duration_ms));
  }
  if (att.artwork_id != null && Number.isFinite(Number(att.artwork_id))) {
    row.artwork_id = Math.trunc(Number(att.artwork_id));
  }
  return row;
}

export const POSTS_V2_BASE = POSTS_V2;
